// Gets a new build onto open tabs and the installed web app without getting in the way.
//
// Finding updates: the browser only checks for a new service worker when a page is loaded.
// An installed web app that is just brought back from the background never loads a page, so
// we ask for an update whenever the app becomes visible and every 30 minutes while it is open.
//
// Reloading: when the new service worker takes over, the page has to reload. That waits until
// it doesn't hurt: nothing holds it (voice recording, sending, files picked) and the user hasn't
// typed or touched anything for a moment (or the app is in the background). Chat drafts are
// saved as they are typed (chatDrafts below), so nothing written is lost.

const CHECK_EVERY_MS = 30 * 60 * 1000;
const MIN_CHECK_GAP_MS = 15 * 1000;
const IDLE_MS = 2500;
const RETRY_MS = 500;

let lastInput = Date.now();
const holds = new Set();
let reloadPending = false;

if (typeof window !== 'undefined') {
  const touched = () => { lastInput = Date.now(); };
  ['keydown', 'pointerdown', 'touchstart', 'input', 'wheel'].forEach(type => window.addEventListener(type, touched, { capture: true, passive: true }));
}

// Blocks the update reload while something must not be interrupted. Returns the release function.
export function holdReload() {
  const token = {};
  holds.add(token);
  return () => holds.delete(token);
}

// Asks the service worker registration for updates when the app becomes visible and periodically
export function watchForUpdates(registration) {
  let lastCheck = Date.now();
  const check = () => {
    if (document.visibilityState !== 'visible' || Date.now() - lastCheck < MIN_CHECK_GAP_MS) return;
    lastCheck = Date.now();
    registration.update().catch(() => {});
  };
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  window.addEventListener('online', check);
  setInterval(check, CHECK_EVERY_MS);
}

// Reloads once a new build took over, as soon as it is safe
export function reloadWhenSafe() {
  if (reloadPending) return;
  reloadPending = true;
  const attempt = () => {
    if (holds.size) return;
    if (document.visibilityState === 'visible' && Date.now() - lastInput < IDLE_MS) return;
    clearInterval(timer);
    window.location.reload();
  };
  const timer = setInterval(attempt, RETRY_MS);
  document.addEventListener('visibilitychange', attempt);
  attempt();
}

/* ── Chat drafts: kept per chat for this app session, so a reload (or switching chats) keeps them ── */

const DRAFT_PREFIX = 'chat_draft:';

export const chatDrafts = {
  load(key) {
    if (!key) return '';
    try { return sessionStorage.getItem(DRAFT_PREFIX + key) || ''; } catch { return ''; }
  },
  save(key, text) {
    if (!key) return;
    try {
      if (text) sessionStorage.setItem(DRAFT_PREFIX + key, text);
      else sessionStorage.removeItem(DRAFT_PREFIX + key);
    } catch {}
  },
};
